import { isRecord, responseErrorState } from './entityResponse'

export const ACCOUNT_VALUE_KEYS = Object.freeze([
  'debet_start',
  'credit_start',
  'debet_period',
  'credit_period',
  'debet_end',
  'credit_end'
])

export const EMPTY_ACCOUNT_SUMMARY = Object.freeze({
  debet_start: null,
  credit_start: null,
  debet_period: null,
  credit_period: null,
  debet_end: null,
  credit_end: null
})

export const EMPTY_CARD_SUMMARY = Object.freeze({
  saldo_debet_start: null,
  saldo_credit_start: null,
  debet_end: null,
  credit_end: null,
  saldo_debet_end: null,
  saldo_credit_end: null,
  saldo_debet_start_count: null,
  saldo_credit_start_count: null,
  saldo_debet_end_count: null,
  saldo_credit_end_count: null
})

function withStableId(row, fallback) {
  return { ...row, id: row.id === undefined || row.id === null ? fallback : row.id }
}

export function normalizeBillsResponse(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить план счетов') }
  }
  if (!Array.isArray(response.bills)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректный план счетов' }
  }
  return {
    ok: true,
    bills: response.bills.filter(isRecord),
    summary: isRecord(response.summary) ? response.summary : { ...EMPTY_ACCOUNT_SUMMARY }
  }
}

export function flattenAccounts(accounts, expanded = new Set(), level = 0, parent = null) {
  const result = []
  accounts.forEach((source, index) => {
    if (!isRecord(source)) return
    const row = withStableId(source, `${parent ? parent.id : 'root'}-${index}`)
    const children = Array.isArray(source.subbils) ? source.subbils.filter(isRecord) : []
    result.push({
      ...row,
      level,
      parent_id: parent ? parent.id : null,
      has_children: children.length > 0,
      is_expanded: children.length > 0 && expanded.has(String(row.id)),
      full_name: String(row.full_name || row.name || 'Счет без названия'),
      name: String(row.name || ''),
      description: String(row.description || '')
    })
    if (children.length && expanded.has(String(row.id))) {
      result.push(...flattenAccounts(children, expanded, level + 1, row))
    }
  })
  return result
}

export function flattenAllAccounts(accounts, level = 0, parent = null) {
  const expanded = new Set()
  const collect = (rows) => rows.forEach((row) => {
    if (!isRecord(row)) return
    expanded.add(String(row.id))
    if (Array.isArray(row.subbils)) collect(row.subbils)
  })
  collect(accounts)
  return flattenAccounts(accounts, expanded, level, parent)
}

export function accountHasMovement(account) {
  return ACCOUNT_VALUE_KEYS.some((key) => {
    const value = Number(account[key])
    return Number.isFinite(value) && value !== 0
  })
}

export function normalizeCompactBillsResponse(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить список счетов') }
  }
  if (!Array.isArray(response.bills)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректный список счетов' }
  }
  return {
    ok: true,
    bills: response.bills.filter(isRecord).map((bill, index) => ({
      ...withStableId(bill, `account-${index}`),
      full_name: String(bill.full_name || bill.display_name || bill.name || 'Счет без названия'),
      description: String(bill.description || ''),
      analytic_fields: isRecord(bill.analytic) && Array.isArray(bill.analytic.fields)
        ? bill.analytic.fields.filter(isRecord)
        : []
    }))
  }
}

export function normalizeSaldoResponse(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось сформировать ОСВ') }
  }
  if (!Array.isArray(response.data)) {
    return { ok: false, state: 'error', message: 'Сервер вернул некорректные данные ОСВ' }
  }
  return {
    ok: true,
    rows: response.data.filter(isRecord),
    summary: isRecord(response.summary) ? response.summary : {},
    billName: String(response.bill_name || ''),
    shouldShowCount: response.should_show_count === true,
    filterValues: Array.isArray(response.first_level_filter_values)
      ? response.first_level_filter_values.map(String)
      : []
  }
}

export function flattenSaldoRows(rows, level = 0, parentId = null) {
  const result = []
  rows.forEach((source, index) => {
    if (!isRecord(source)) return
    const id = source.id === undefined || source.id === null ? `${parentId || 'saldo'}-${index}` : source.id
    result.push({
      ...source,
      id,
      level,
      parent_id: parentId,
      label: String(source.label || source.name || 'Без названия'),
      model_type: String(source.model_type || '')
    })
    if (Array.isArray(source.childs)) result.push(...flattenSaldoRows(source.childs, level + 1, id))
  })
  return result
}

export function normalizeFinanceSummary(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить финансовую сводку') }
  }
  return {
    ok: true,
    cards: isRecord(response.cards) ? response.cards : {},
    calculatedAt: response.calculated_at || null,
    isStale: response.is_stale === true,
    diagnostics: Array.isArray(response.diagnostics) ? response.diagnostics.filter(isRecord) : []
  }
}

export function normalizeDocumentsResponse(response) {
  if (!isRecord(response) || response.type === 'error' || response.error) {
    return { ok: false, ...responseErrorState(response, 'Не удалось загрузить последние документы') }
  }
  return {
    ok: true,
    rows: Array.isArray(response.data) ? response.data.filter(isRecord) : []
  }
}

export function documentRoute(row) {
  if (!row || row.id === undefined || row.id === null) return null
  const names = {
    upd: 'document_info_upd',
    payment: 'document_info_payment',
    avance: 'document_info_factura_avance',
    act_sver: 'act_check_info'
  }
  const name = names[row.document_type]
  return name ? { name, params: { id: row.id } } : null
}

function csvCell(value) {
  return `"${String(value === undefined || value === null ? '' : value).replace(/"/g, '""')}"`
}

export function downloadCsv(filename, columns, rows) {
  const body = [
    columns.map((column) => csvCell(column.label)).join(';'),
    ...rows.map((row) => columns.map((column) => csvCell(row[column.name])).join(';'))
  ].join('\n')
  const link = document.createElement('a')
  link.href = URL.createObjectURL(new Blob([`\ufeff${body}`], { type: 'text/csv;charset=utf-8' }))
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
